/**
 * 弹窗配置接口
 */
export interface PopupConfig {
  /** 弹窗唯一标识 */
  id: string;
  /** 优先级（数字越小优先级越高） */
  priority: number;
  /** 弹窗名称（用于调试和日志） */
  name: string;
  /** 弹窗描述 */
  description?: string;
}

/**
 * 所有弹窗的统一配置
 */
export const POPUP_CONFIGS: Record<string, PopupConfig> = {
  // 优先级最高的弹窗（在所有其他弹窗之前弹出）
  AGE_VERIFICATION: {
    id: "age-verification",
    priority: 1,
    name: "age verification",
    description: "age verification popup",
  },
  AGREEMENT_CONFIRMATION: {
    id: "agreement-confirmation",
    priority: 2,
    name: "agreement confirmation",
    description: "agreement confirmation popup",
  },
  COUNTRY_LIST: {
    id: "country-list-dialog",
    priority: 3,
    name: "country list",
    description: "country selection popup",
  },

  // 系统级弹窗
  WEB_PUSH: {
    id: "web-push",
    priority: 10,
    name: "web push subscription",
    description: "web push subscription popup",
  },
  DOWNLOAD_APP: {
    id: "download-app-popup",
    priority: 20,
    name: "download app",
    description: "download app popup",
  },
  APP_DOWNLOAD_DIALOG: {
    id: "app-download-dialog",
    priority: 22,
    name: "app download dialog",
    description: "app download dialog (manual trigger only)",
  },

  // 其他系统弹窗
  CHECK_IN: {
    id: "check-in-dialog",
    priority: 21,
    name: "check in",
    description: "check in popup",
  },
  // 自定义进入首页弹窗
  CUSTOM_HOME_POPUP: {
    id: "custom-home-popup",
    priority: 25,
    name: "custom home popup",
    description: "custom home popup with swipeable content",
  },

  // Event 相关弹窗
  FIRST_RECHARGE: {
    id: "first-recharge-bonus",
    priority: 30,
    name: "first recharge bonus",
    description: "first recharge bonus popup",
  },
  CASHBACK: {
    id: "cashback-bonus",
    priority: 31,
    name: "cashback bonus",
    description: "cashback bonus popup",
  },
  RECHARGE_OFFER: {
    id: "recharge-offer",
    priority: 32,
    name: "recharge offer",
    description: "recharge offer popup",
  },
  RED_PACKET: {
    id: "red-packet-rain",
    priority: 33,
    name: "red packet rain",
    description: "red packet rain popup",
  },
  RECHARGE_PROMOTION: {
    id: "recharge-promotion",
    priority: 34,
    name: "recharge promotion",
    description: "recharge promotion popup",
  },
  BET_ACTIVITY: {
    id: "bet-activity",
    priority: 35,
    name: "bet activity",
    description: "bet activity popup",
  },
  SEVEN_DAY_RECHARGE: {
    id: "seven-day-recharge",
    priority: 36,
    name: "seven day recharge",
    description: "seven day recharge activity popup",
  },
  RESCUE_FUND: {
    id: "rescue-fund",
    priority: 38,
    name: "rescue fund",
    description: "rescue fund popup",
  },
  GRID_LOTTERY: {
    id: "grid-lottery",
    priority: 37,
    name: "grid lottery",
    description: "nine grid lottery activity popup",
  },

  VIP_UP: {
    id: "vip-up-dialog",
    priority: 50,
    name: "vip up",
    description: "vip up popup",
  },
  POPUP_BANNER: {
    id: "popup-banner-dialog",
    priority: 60,
    name: "banner",
    description: "banner popup",
  },
} as const;

/**
 * 弹窗ID映射（便于使用）
 */
export const POPUP_IDS = Object.keys(POPUP_CONFIGS).reduce(
  (acc, key) => {
    acc[key] = POPUP_CONFIGS[key].id;
    return acc;
  },
  {} as Record<string, string>
) as {
  readonly WEB_PUSH: string;
  readonly DOWNLOAD_APP: string;
  readonly FIRST_RECHARGE: string;
  readonly CASHBACK: string;
  readonly RECHARGE_OFFER: string;
  readonly RED_PACKET: string;
  readonly RECHARGE_PROMOTION: string;
  readonly BET_ACTIVITY: string;
  readonly SEVEN_DAY_RECHARGE: string;
  readonly RESCUE_FUND: string;
  readonly GRID_LOTTERY: string;
  readonly CHECK_IN: string;
  readonly VIP_UP: string;
  readonly POPUP_BANNER: string;
};

/**
 * 根据弹窗ID获取配置
 */
export const getPopupConfig = (popupId: string): PopupConfig | undefined => {
  return Object.values(POPUP_CONFIGS).find(config => config.id === popupId);
};

/**
 * 根据配置键获取弹窗配置
 */
export const getPopupConfigByKey = (
  key: keyof typeof POPUP_CONFIGS
): PopupConfig => {
  return POPUP_CONFIGS[key];
};

/**
 * 获取所有弹窗配置列表（按优先级排序）
 */
export const getAllPopupConfigs = (): PopupConfig[] => {
  return Object.values(POPUP_CONFIGS).sort((a, b) => a.priority - b.priority);
};

/**
 * 获取指定优先级的弹窗列表
 */
export const getPopupsByPriority = (priority: number): PopupConfig[] => {
  return Object.values(POPUP_CONFIGS).filter(
    config => config.priority === priority
  );
};

/**
 * 验证弹窗ID是否存在
 */
export const isValidPopupId = (popupId: string): boolean => {
  return Object.values(POPUP_CONFIGS).some(config => config.id === popupId);
};
